import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema, collectionPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import {
  Factory, Landmark, Hotel, HeartPulse, GraduationCap, Building2,
  Cpu, Scale, Anchor, Truck, ArrowRight, Phone,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PAGE_URL = `${SITE}/industries`;
const TITLE = "Industry Hub — บริการกฎหมาย·แปล·วีซ่า แยกตามอุตสาหกรรม | NYC Language Institute";
const DESC =
  "บริการเฉพาะอุตสาหกรรม 10 sectors — Manufacturing, Fintech, Hospitality, Healthcare, Education, Real Estate, Tech/SaaS, Legal Firms — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";

type Industry = {
  slug: string;
  icon: React.ComponentType<{ className?: string }>;
  name: string;
  tagline: string;
  audience: string;
  services: { label: string; href: string }[];
  highlights: string[];
};

const INDUSTRIES: Industry[] = [
  {
    slug: "manufacturing",
    icon: Factory,
    name: "Manufacturing & Industrial",
    tagline: "BOI · ISO · Export Compliance",
    audience: "โรงงาน · OEM · ผู้ส่งออก",
    services: [
      { label: "BOI Application & Work Permit", href: "/services/work-permit-boi" },
      { label: "Trademark / IP Registration", href: "/services/trademark-ip" },
      { label: "Technical Translation (50+ ภาษา)", href: "/services/translation" },
      { label: "MFA Legalization", href: "/services/mfa-legalization" },
    ],
    highlights: ["รับรองเอกสาร ISO 9001 / 14001", "Customs Documents", "Bilingual Contracts (EN-TH-CN-JP)"],
  },
  {
    slug: "fintech-banking",
    icon: Landmark,
    name: "Fintech & Banking",
    tagline: "PDPA · BOT · KYC Compliance",
    audience: "ธนาคาร · บริษัทหลักทรัพย์ · Fintech Startup",
    services: [
      { label: "PDPA & Cybersecurity", href: "/services/pdpa-cybersecurity" },
      { label: "Banking & Finance Law", href: "/services/banking-finance" },
      { label: "Sworn Translation", href: "/services/sworn-translation" },
      { label: "M&A / Private Equity", href: "/services/ma-private-equity" },
    ],
    highlights: ["BOT Reporting Translation", "AML/KYC Document Pack", "Cross-border Contracts"],
  },
  {
    slug: "hospitality-tourism",
    icon: Hotel,
    name: "Hospitality & Tourism",
    tagline: "Hotel · Restaurant · Tour Operator",
    audience: "โรงแรม · ร้านอาหาร · บริษัททัวร์",
    services: [
      { label: "Work Permit (Non-B)", href: "/services/work-permit-boi" },
      { label: "Marriage Visa (Thai-Foreign)", href: "/services/marriage-thai-foreign" },
      { label: "Labor & Employment Law", href: "/services/labor-employment" },
      { label: "Menu/Brochure Translation", href: "/services/translation" },
    ],
    highlights: ["TAT License Support", "Liquor/Hotel License", "Foreign Staff Onboarding"],
  },
  {
    slug: "healthcare-pharma",
    icon: HeartPulse,
    name: "Healthcare & Pharma",
    tagline: "FDA · Medical Records · Clinical Trials",
    audience: "โรงพยาบาล · คลินิก · บริษัทยา",
    services: [
      { label: "Medical Translation (NAATI)", href: "/services/naati" },
      { label: "Sworn Translation (Embassy)", href: "/services/sworn-translation" },
      { label: "Notary Public", href: "/services/notary-public" },
      { label: "Apostille (Hague)", href: "/services/apostille" },
    ],
    highlights: ["Clinical Trial Protocols", "FDA Submission Translation", "Patient Records (HIPAA-aware)"],
  },
  {
    slug: "education-academia",
    icon: GraduationCap,
    name: "Education & Academia",
    tagline: "Transcripts · Diplomas · Research",
    audience: "นักเรียน · นักศึกษา · มหาวิทยาลัย",
    services: [
      { label: "Transcript Translation", href: "/services/translation" },
      { label: "MFA + Embassy Legalization", href: "/services/mfa-legalization" },
      { label: "Apostille", href: "/services/apostille" },
      { label: "Student Visa Support", href: "/services/visa-thai-citizens" },
    ],
    highlights: ["ใบ ปริญญา/ทรานสคริปต์", "Police Clearance (CID)", "Affidavit of Support"],
  },
  {
    slug: "real-estate",
    icon: Building2,
    name: "Real Estate & Construction",
    tagline: "Foreign Ownership · Lease · Land Office",
    audience: "นายหน้า · นักลงทุน · Developer",
    services: [
      { label: "Real Estate Conveyancing", href: "/services/real-estate-conveyancing" },
      { label: "Sale & Purchase Agreement (EN-TH)", href: "/services/translation" },
      { label: "Notary Public (POA)", href: "/services/notary-public" },
      { label: "Civil & Family Law", href: "/services/civil-family-probate" },
    ],
    highlights: ["Title Deed Translation", "Usufruct/Lease 30-Year", "Condo Foreign Quota Verification"],
  },
  {
    slug: "tech-saas",
    icon: Cpu,
    name: "Tech & SaaS",
    tagline: "DTV · Smart Visa · IP · PDPA",
    audience: "Startup · Digital Nomad · Tech Company",
    services: [
      { label: "DTV (Digital Nomad Visa)", href: "/visa-comparison" },
      { label: "Smart Visa / LTR", href: "/services/ltr-elite-visa" },
      { label: "Trademark & Software IP", href: "/services/trademark-ip" },
      { label: "PDPA Compliance Audit", href: "/services/pdpa-cybersecurity" },
    ],
    highlights: ["Terms of Service (Bilingual)", "DPA / SCC Drafting", "BOI for Tech Startups"],
  },
  {
    slug: "legal-firms",
    icon: Scale,
    name: "Legal Firms & In-House Counsel",
    tagline: "White-label Translation · Notarization",
    audience: "สำนักงานกฎหมาย · นิติกร",
    services: [
      { label: "Sworn Translation (Court)", href: "/services/sworn-translation" },
      { label: "Notary Public (6 ทนาย)", href: "/services/notary-public" },
      { label: "Criminal Defense", href: "/services/criminal-defense" },
      { label: "International Arbitration", href: "/services/international-arbitration" },
    ],
    highlights: ["Wholesale Pricing (50+ pages)", "Net-30 Billing", "NDA / Conflict-of-Interest Check"],
  },
  {
    slug: "logistics-customs",
    icon: Truck,
    name: "Logistics & Customs",
    tagline: "Import/Export · Customs · Freight",
    audience: "Shipping · Freight Forwarder · Importer",
    services: [
      { label: "Customs Document Translation", href: "/services/translation" },
      { label: "MFA Legalization", href: "/services/mfa-legalization" },
      { label: "Tax Law (Import Duties)", href: "/services/tax-law" },
      { label: "Bill of Lading Notarization", href: "/services/notary-public" },
    ],
    highlights: ["HS Code Translation", "Free Trade Agreement Docs", "Customs Dispute Resolution"],
  },
  {
    slug: "marine-yacht",
    icon: Anchor,
    name: "Marine & Yacht",
    tagline: "Vessel · Crew Visa · Charter",
    audience: "เจ้าของเรือ · Yacht Broker · Captain",
    services: [
      { label: "Crew Visa & Work Permit", href: "/services/work-permit-boi" },
      { label: "Vessel Registration Translation", href: "/services/translation" },
      { label: "Notary Public (Yacht Bill of Sale)", href: "/services/notary-public" },
      { label: "Maritime Insurance Contract", href: "/services/banking-finance" },
    ],
    highlights: ["Charter Agreement (EN-TH)", "Customs Yacht Importation", "Phuket / Pattaya On-site"],
  },
];

const FAQS = [
  { q: "บริการ Industry Hub แตกต่างจากบริการทั่วไปอย่างไร?", a: "Industry Hub คือบริการที่ออกแบบเฉพาะอุตสาหกรรม — ทีมงานเข้าใจ vocabulary, compliance, และเอกสารมาตรฐานของแต่ละ sector ทำให้ส่งงานได้เร็วและถูกต้องตามมาตรฐานของหน่วยงานกำกับ" },
  { q: "ราคาคิดเป็น Package หรือต่อหน้า?", a: "ลูกค้าองค์กรในแต่ละอุตสาหกรรมสามารถเลือกได้ทั้ง Per-document (มาตรฐาน) และ Retainer รายเดือน/รายปี ลดได้สูงสุด 25% สำหรับงานปริมาณมาก พร้อม Account Manager เฉพาะกลุ่ม" },
  { q: "มี NDA และ Compliance Audit หรือไม่?", a: "มี — NDA มาตรฐาน ISO 17100:2015 ข้อ 5.5 พร้อมข้อ Liquidated Damages และ PDPA Compliance Audit ตาม พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล 2562 ม.37–41 ครอบคลุม Fintech (พ.ร.บ.ธุรกรรมอิเล็กทรอนิกส์), Healthcare (HIPAA/พ.ร.บ.สุขภาพแห่งชาติ) และ SaaS (SOC 2 Type II) โดยทนายความเฉพาะทาง — ราคา audit เริ่ม 25,000 บาท ออก report 7 วัน" },
  { q: "ส่งงานข้ามอุตสาหกรรมได้ไหม?", a: "ได้ — Account Manager ประสานข้าม sector เช่น Real Estate × Tax × Translation ใน Property Deal เดียวกัน หรือ M&A ที่ผสาน Due Diligence (ป.พ.พ. ม.453+), Tax Restructuring (ป.รัษฎากร ม.74), แปล MFA + Apostille (Hague 15 ธ.ค. 2025) — ใช้ SPOC เดียว, billing รวม, SLA 24 ชม., รับงานคู่ขนานสูงสุด 8 ภาษา" },
  { q: "มีบริการนอกกรุงเทพฯ ไหม?", a: "ครอบคลุม 77 จังหวัด ผ่านสาขาขอนแก่น อุดรธานี หนองคาย และพันธมิตรในจังหวัดอื่นๆ — สามารถจัดทีมเข้าหน้างานได้ทั่วประเทศภายใน 48 ชม." },
];

export const Route = createFileRoute("/industries/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/industries", "th")},
      { name: "description", content: ctaDesc(DESC, "/industries", "th")},
      { name: "keywords", content: "industry hub, บริการกฎหมายตามอุตสาหกรรม, manufacturing BOI, fintech compliance, hospitality work permit, healthcare translation" },
      { property: "og:title", content: ctaTitle(TITLE, "/industries", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/industries", "th")},
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: PAGE_URL },
      ...hreflangLinks("/industries"),
    ],
    scripts: [speakableSchema(), consolidateGraph([
      collectionPageSchema({ path: "/industries", name: TITLE, description: DESC, itemCount: 10 }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "NYC Legal & Notary — Industry Hub",
          url: PAGE_URL,
          numberOfItems: INDUSTRIES.length,
          itemListElement: INDUSTRIES.map((i, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: i.name,
            url: `${PAGE_URL}#${i.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "Industry Hub", item: PAGE_URL },
          ],
        }),
      },
    ])],
  }),
  component: IndustriesPage,
});

function IndustriesPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] via-[#0d2150] to-[#0a1a3d] px-4 py-14 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
            🏢 Industry Hub · 10 Sectors
          </span>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            บริการกฎหมาย · แปล · วีซ่า<br />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              ออกแบบเฉพาะแต่ละอุตสาหกรรม
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/80 sm:text-lg">
            10 อุตสาหกรรมหลัก — ตั้งแต่ Manufacturing & BOI ไปจนถึง Fintech Compliance และ Marine Crew Visa
            ทีมงานเฉพาะทางที่เข้าใจ vocabulary, compliance และเอกสารมาตรฐานของ sector คุณ
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep shadow-xl shadow-amber-500/30 hover:bg-amber-400">
                ขอใบเสนอราคา Industry Package
              </button>
            } />
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
              <Phone className="h-4 w-4" /> ปรึกษาฟรี 083-249-4999
            </a>
          </div>
        </div>
      </section>

      {/* Industry index pills */}
      <section className="border-b border-white/10 bg-[#0a1a3d]/80 px-4 py-5 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap gap-2">
          {INDUSTRIES.map((i) => (
            <a key={i.slug} href={`#${i.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-white/80 hover:border-amber-400/40 hover:bg-amber-500/10 hover:text-amber-200">
              <i.icon className="h-3.5 w-3.5" /> {i.name}
            </a>
          ))}
        </div>
      </section>

      {/* Industry cards */}
      <section className="px-4 py-14 sm:py-16">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
          {INDUSTRIES.map((ind) => (
            <article
              key={ind.slug}
              id={ind.slug}
              className="scroll-mt-24 rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-6 shadow-xl sm:p-8"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-500/15 ring-1 ring-amber-400/30">
                  <ind.icon className="h-7 w-7 text-amber-300" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-extrabold leading-tight">{ind.name}</h2>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-amber-300/90">{ind.tagline}</p>
                  <p className="mt-1 text-sm text-white/80">เหมาะกับ: {ind.audience}</p>
                </div>
              </div>

              <div className="mt-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-white/80">บริการหลัก</p>
                <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                  {ind.services.map((s) => (
                    <Link key={s.label} to={s.href} className="group flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white/85 transition-all hover:border-amber-400/40 hover:bg-amber-500/10 hover:text-amber-200">
                      <span className="truncate">{s.label}</span>
                      <ArrowRight className="h-3 w-3 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-1.5">
                {ind.highlights.map((h) => (
                  <span key={h} className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-white/70">
                    ✓ {h}
                  </span>
                ))}
              </div>

              <Link
                to="/industries/$slug"
                params={{ slug: ind.slug }}
                className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-2 text-xs font-bold text-amber-200 transition hover:bg-amber-500/20"
              >
                ดูโซลูชันเต็มสำหรับ {ind.name} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Industrial estates cross-link */}
      <section className="border-t border-white/10 bg-[#0a1b40] px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">บริการในนิคมอุตสาหกรรม 20+ แห่ง</h2>
          <p className="mt-2 text-sm text-white/70">รับเอกสารถึงโรงงานใน EEC, อมตะ, WHA, มาบตาพุด, ลาดกระบัง, นวนคร ฯลฯ — daily courier</p>
          <Link
            to="/industrial-estates"
            className="mt-5 inline-flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-5 py-3 text-sm font-bold text-amber-200 transition hover:bg-amber-500/20"
          >
            ดูนิคมทั้งหมด <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>



      {/* FAQ */}
      <section className="border-t border-white/10 bg-[#0d2150] px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">คำถามที่พบบ่อย — Industry Hub</h2>
          <div className="mt-6 space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 open:border-amber-400/30 open:bg-white/[0.06]">
                <summary className="cursor-pointer list-none text-sm font-bold text-white marker:hidden">
                  <span className="mr-2 text-amber-300">Q.</span>{f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-white/75">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">ต้องการ Industry Package เฉพาะธุรกิจคุณ?</h2>
          <p className="mt-3 text-white/75">Account Manager เฉพาะ sector + Retainer รายเดือน/รายปี ลดสูงสุด 25%</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
                ขอใบเสนอราคา · ฟรี
              </button>
            } />
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
